import withTranslate, { WithTranslateProps } from "../common/WithTranslate";
import { inject, observer } from "mobx-react";
import React from "react";
import {
    Column,
    DataGrid,
    Button as DGButton,
    Export,
    FilterPanel,
    FilterRow,
    HeaderFilter,
    Pager,
    Paging,
    SearchPanel,
    Sorting,
} from 'devextreme-react/data-grid';
import { ActionCode, Page, TLCode } from "../../common/enums";
import styled from "@emotion/styled";
import { Instance } from "mobx-state-tree";
import { ApplicationStore, AuthenticationStore, SecretariatStore, UserStore } from "../../store";
import DataSource from "devextreme/data/data_source";

interface SecretatiatUsersTabProps extends WithTranslateProps {
    ApplicationStore?: Instance<typeof ApplicationStore>;
    UserStore?: Instance<typeof UserStore>;
    secretariatId: number;
    AuthenticationStore?: Instance<typeof AuthenticationStore>;
    SecretariatStore?: Instance<typeof SecretariatStore>;
}

interface SecretatiatUsersTabState {
    isLoading: boolean;
    dataSource?: DataSource;
}

@inject('SecretariatStore')
@inject('AuthenticationStore')
@inject('ApplicationStore')
@inject('UserStore')
@observer
class SecretatiatUsersTab extends React.Component<SecretatiatUsersTabProps, SecretatiatUsersTabState> {
    state: SecretatiatUsersTabState = {
        isLoading: false,
        dataSource: undefined
    };

    _isMounted = false;

    async componentDidMount() {
        this._isMounted = true;
        this._isMounted && this.setState({ isLoading: true });

        const dataSource = await this.buildGridData();
        if (this._isMounted) {
            this.setState({
                isLoading: false,
                dataSource: dataSource
            });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    showUser = (user: any) => {
        if (!this.props.AuthenticationStore || !this.props.SecretariatStore)
            return false;

        return (
            (this.props.AuthenticationStore.isSuperAdmin && this.props.SecretariatStore.selectedSecretariatId === 0) ||
            (this.props.AuthenticationStore.isSuperAdmin &&
                user !== undefined &&
                this.props.SecretariatStore.selectedSecretariatId !== undefined &&
                this.props.SecretariatStore.selectedSecretariatId === user.secretaryId) ||
            (user !== undefined &&
                this.props.AuthenticationStore.isSecretariat &&
                this.props.AuthenticationStore.secretariatId === user.secretaryId) ||
            (this.props.AuthenticationStore.isAdmin &&
                !user.roles.find((r: any) => r.name === ActionCode.ADMIN_CODE || r.name === ActionCode.SUPER_ADMIN_CODE))
        );
    };

    buildGridData = async (): Promise<DataSource | undefined> => {
        if (!this.props.UserStore)
            return undefined;

        try {
            const criteria: any = { secretariatId: this.props.secretariatId, withRoles: true };
            const users = await this.props.UserStore.searchWithRoles(criteria);
            if (users == undefined || users.length === 0) {
                return undefined;
            }

            const data = users
                .filter((u: any) => this.showUser(u))
                .map((user: any) => {
                    return {
                        id: user.userId,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        userName: user.userName,
                        email: user.email,
                        roles: user.roles,
                        status: user.isActive,
                    };
                });

            return new DataSource({
                store: { type: 'array', key: 'id', data: data },
                langParams: {
                    locale: 'fr',
                    collatorOptions: {
                        sensitivity: 'base',
                    },
                },
            });
        } catch (error) {
            console.error('Error building grid data:', error);
            return undefined;
        }
    };

    editUser = (e: any) => {
        if (this.props.ApplicationStore) {
            this.props.ApplicationStore.navigate(`${Page.UserPage}/${e.row.data.id}`);
        }
    };

    onRowDoubleClicked = (ev) => {
        if (this.props.ApplicationStore) {
            this.props.ApplicationStore.navigate(`${Page.UserPage}/${ev.data.id}`);
        }
    };

    render() {
        const { translate } = this.props;
        const { dataSource, isLoading } = this.state;

        return (
            <ContainerCol>
                <DivLine>
                    <DivLeft />
                    <DivRight>
                    </DivRight>
                </DivLine>
                <DataGrid
                    id="usersList"
                    dataSource={dataSource}
                    showBorders
                    allowColumnResizing
                    width={'100%'}
                    rowAlternationEnabled={true}
                    onRowDblClick={this.onRowDoubleClicked}
                >
                    <Export enabled={true} texts={{ exportAll: translate(TLCode.GRID_EXCEL_EXPORT) }} />
                    <Sorting mode="multiple" />
                    <FilterPanel visible={true} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} applyFilter={'auto'} />
                    <SearchPanel visible={true} highlightCaseSensitive={true} width={400} />
                    <Column
                        dataType="string"
                        dataField="userName"
                        caption={translate(TLCode.Username)}
                        minWidth={200}
                        defaultSortOrder="asc"
                        sortIndex={1}
                    />
                    <Column
                        dataType="string"
                        dataField="lastName"
                        caption={translate(TLCode.Lastname)}
                        minWidth={200}
                    />
                    <Column
                        dataType="string"
                        dataField="firstName"
                        caption={translate(TLCode.Firstname)}
                        minWidth={200}
                    />
                    <Column dataType="string" dataField="email" caption={translate(TLCode.Email)} minWidth={200} />
                    <Column dataType="boolean" dataField="status" caption={translate(TLCode.IS_ACTIVE)} width={80} />
                    <Column
                        dataType="string"
                        dataField="roles"
                        caption={translate(TLCode.Roles)}
                        minWidth={200}
                        calculateDisplayValue={(r: any) => r.roles.map((role: any) => role.name).join(', ')}
                        calculateCellValue={(r: any) => r.roles.map((role: any) => role.name).join(', ')}
                    />
                    <Column
                        caption="Actions"
                        type="buttons"
                        width={120}
                    >
                        <DGButton
                            hint={translate(TLCode.GENERAL_Edit)}
                            icon={'user'}
                            onClick={this.editUser}
                        />
                    </Column>
                    <Pager
                        visible={true}
                        allowedPageSizes={[10, 20, 50]}
                        showPageSizeSelector={true}
                        showInfo={true}
                        showNavigationButtons={true}
                    />
                    <Paging defaultPageSize={20} />
                </DataGrid>

                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        Chargement...
                    </div>
                )}
            </ContainerCol>
        );
    }
}

const ContainerCol = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 5px;
  .bp3-icon {
    margin-right: 3px;
  }
`;

export default withTranslate(SecretatiatUsersTab);